/**
 * themeLibraryLive — the parts of the theme library that change between
 * builds: status, the last proof, provenance, and the themes the house does
 * not ship. (CO 680 Phase 2b)
 *
 * /themes draws its swatches from a module generated from the house supply
 * (themes-page-data.ts), which is right for a house theme's colors and wrong
 * for its status: a theme is `proven` only while a stored proof matches the
 * bytes being served, and that is derived on every read by
 * `rendering_theme_library`, never built. The page reads the library once,
 * tokenless, and lays the answer over the built cards by ref. If the read
 * fails the page is the build-time page: every card reads `untested`.
 *
 * With a tenant id the same read also returns that tenant's library — under
 * L10, a tokenless caller sees only the themes the tenant has assigned.
 */

const GATEWAY_URL =
  import.meta.env.VITE_FEDERATION_GATEWAY_URL ||
  "https://mcp.comos-gateway.com";

/** One library entry as `rendering_theme_library` serves it (listing level; no body). */
export interface LiveThemeEntry {
  ref: string;
  kind: "tokens" | "css";
  origin: "house" | "market" | "tenant";
  scope: "public" | "tenant";
  owner_tenant_id: string;
  title: string;
  /** css: the tokens ref it layers on; tokens: null. */
  base: string | null;
  sha256: string;
  bytes: number;
  glass: boolean;
  status: "untested" | "proven";
  proven_on: Array<{ place: string; source: "live" | "fixture" }>;
  last_proof: {
    at: string;
    passed: boolean;
    cells: number;
    failed_cells: number;
    revision: string;
  } | null;
  made_by: { prompt_id: string; sha256: string; run_id: string } | null;
  copied_from: string | null;
  published_as: string | null;
  /** Tenant entries: where the theme draws (`default`, `view:<name>`, `page:<name>`). */
  assigned: string[];
  created_at: string | null;
  updated_at: string | null;
}

async function callPublicTool<T>(tool: string, args: unknown): Promise<T> {
  const res = await fetch(`${GATEWAY_URL}/tools/${tool}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!res.ok) throw new Error(`${tool} read failed: ${res.status}`);
  const wire = (await res.json()) as {
    result?: { content?: Array<{ text?: string }> };
  };
  const text = wire.result?.content?.[0]?.text;
  if (typeof text !== "string") throw new Error(`${tool} read: no content`);
  return JSON.parse(text) as T;
}

/** The library's largest page; the house and market together are far below it. */
const LIMIT = 200;

/**
 * Every entry the library serves a tokenless reader: the house and market, and
 * with `tenantId` that tenant's assigned themes too. Throws on any failure.
 */
export async function fetchThemeLibrary(tenantId?: string): Promise<LiveThemeEntry[]> {
  const body = await callPublicTool<{
    ok?: boolean;
    groups?: Array<{ entries?: LiveThemeEntry[] }>;
  }>("rendering_theme_library", {
    limit: LIMIT,
    ...(tenantId ? { tenant_id: tenantId } : {}),
  });
  if (body.ok !== true || !Array.isArray(body.groups)) {
    throw new Error("theme library read: not ok");
  }
  const entries: LiveThemeEntry[] = [];
  for (const g of body.groups) {
    for (const e of g.entries ?? []) {
      if (typeof e.ref === "string" && typeof e.sha256 === "string") entries.push(e);
    }
  }
  return entries;
}
